import { styled } from "@mui/material/styles";
import { TableContainer, TableRow, Box, Menu, Table } from "@mui/material";
import InputField from "../InputField/InputField";
import Dropdown from "../Dropdown/Dropdown";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: "none",
  marginTop: "10px",
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: "0 3px",
    marginBottom: "-4px",
  },
  "& .MuiTableHead-root": {
    marginBottom: "8px",
    "& .MuiTableRow-root": {
      overflow: "visible",
    },
  },
}));

export const HeaderRow = styled(TableRow)(({ theme }) => ({
  height: "42px",
  backgroundColor: "transparent",
  marginBottom: "12px",
  "& th": {
    borderBottom: "none",
    padding: "8px 12px",
    borderRight: "none",
    overflow: "visible",
  },
}));

export const HeaderCell = styled("th")(({ theme }) => ({
  backgroundColor: "#3F49A7",
  color: "#fff",
  padding: "8px 12px",
  fontSize: "0.6rem",
  fontWeight: 800,
  textAlign: "center",
  height: "42px",
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  "&:first-of-type": {
    borderTopLeftRadius: "8px",
  },
  "&:last-child": {
    borderTopRightRadius: "8px",
  },
  "&.date-cell": {
    textAlign: "center",
    padding: "4px 0px",
    minWidth: "52px",
    width: "52px",
    height: "42px",
    marginTop: "3px",
    marginBottom: "3px",
    border: "none",
    "& .day": {
      fontSize: "0.65rem",
      fontWeight: 700,
      display: "block",
      marginBottom: "1px",
    },
    "& .date": {
      fontSize: "0.55rem",
      display: "block",
      fontWeight: 500,
      opacity: 0.85,
      textTransform: "none",
      letterSpacing: 0,
    },
  },
}));

export const DataRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: "#ffffff",
  height: "28px",
  "& td": {
    padding: "1px 4px",
    borderBottom: "none",
    backgroundColor: "#ffffff",
    "&:first-of-type": {
      borderTopLeftRadius: "8px",
      borderBottomLeftRadius: "8px",
    },
    "&:last-child": {
      borderTopRightRadius: "8px",
      borderBottomRightRadius: "8px",
    },
  },
}));

export const DataCell = styled("td")(({ theme }) => ({
  padding: "1px 3px",
  backgroundColor: "#ffffff",
  borderBottom: "none",
  height: "26px",
  textAlign: "center",
  "&.budget-cell": {
    borderRadius: "4px",
    padding: "4px 6px",
    textAlign: "center",
    fontSize: "0.7rem",
    color: "#666666",
    width: "80px",
    fontWeight: 500,
    "& .budget-text": {
      backgroundColor: "#F0F0F0",
      padding: "4px 8px",
      borderRadius: "4px",
      display: "inline-block",
      minWidth: "45px",
    },
  },
  "&.day-cell": {
    padding: "1px",
    width: "48px",
    height: "28px",
    backgroundColor: "#FFFFFF",
    borderRadius: "0",
    "&:nth-of-type(5), &:nth-of-type(6)": {
      "& .MuiInputBase-root": {
        backgroundColor: "#EBEBEB",
        border: "none",
        "&.Mui-disabled": {
          backgroundColor: "#EBEBEB",
          opacity: 0.8,
        }
      }
    },
    "&:nth-of-type(7), &:nth-of-type(8), &:nth-of-type(9), &:nth-of-type(10), &:nth-of-type(11)": {
      "& .MuiInputBase-root": {
        "&.Mui-disabled": {
          opacity: 0.8,
        }
      }
    },
    "&:nth-of-type(5)": {
      borderTopLeftRadius: "8px",
      borderBottomLeftRadius: "8px",
      backgroundColor: "#FFFFFF",
    },
    "&:nth-of-type(11)": {
      borderTopRightRadius: "8px",
      borderBottomRightRadius: "8px",
    },
  },
  "&.total-cell": {
    borderRadius: "4px",
    padding: "6px 8px",
    textAlign: "center",
    fontSize: "0.75rem",
    color: "#666666",
    width: "80px",
    fontWeight: 500,
  },
  "&.action-cell": {
    padding: "1px 4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: "4px",
    "& .MuiIconButton-root": {
      color: "#FF4444",
      padding: "4px",
      "&:hover": {
        backgroundColor: "rgba(255, 68, 68, 0.04)",
      },
    },
  },
}));

export const TotalCell = styled(DataCell)(({ theme }) => ({
  backgroundColor: "#3F49A7",
  color: "#fff",
  fontSize: "0.75rem",
  fontWeight: 600,
  textAlign: "center",
  padding: "6px 8px",
}));

export const NumberInput = styled(InputField)({
  height: "28px",
  "& .MuiInputBase-root": {
    height: "28px",
    backgroundColor: "transparent",
    textAlign: "center",
    justifyContent: "center",
    width: "100%",
    padding: "0",
    fontSize: "0.8rem",
    borderRadius: "3px",
    transition: "all 0.2s ease",

    "&:hover": {
      borderColor: "#5F80F6",
    },

    "&.Mui-focused": {
      borderColor: "#5F80F6",
      borderWidth: "1.5px",
    },

    "& .MuiInputBase-input": {
      textAlign: "center",
      padding: "2px 1px",
      fontSize: "0.8rem",
      fontWeight: 500,
      color: "#333",
      height: "28px",
      width: "100%",
      minWidth: "32px",
      backgroundColor: "transparent",
      "&::-webkit-inner-spin-button, &::-webkit-outer-spin-button": {
        display: "none",
        margin: 0,
      },
      "-moz-appearance": "textfield",
      "&:focus": {
        outline: "none",
      },
      "&::selection": {
        backgroundColor: "transparent",
      },
      "&:disabled": {
        color: "#666666",
        cursor: "not-allowed",
        "-webkit-text-fill-color": "#666666",
      },
    },
    "&.Mui-disabled": {
      backgroundColor: "transparent",
      "& .MuiOutlinedInput-notchedOutline": {
        border: "1px solid #EBEBEB",
      }
    },
  },
});

export const TotalHoursBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#F0F0F0",
  padding: "4px 8px",
  borderRadius: "4px",
  color: "#666666",
  fontSize: "0.7rem",
  fontWeight: 500,
  textAlign: "center",
  minWidth: "45px",
  display: "inline-block",
  border: "none",
  height: "auto",
  marginLeft: "18px",
  "&:hover": {
    backgroundColor: "#F0F0F0",
  },
}));

export const TotalRow = styled(TableRow)(({ theme }) => ({
  "& td": {
    backgroundColor: "#3F49A7",
    color: "#fff",
    fontSize: "0.8rem",
    fontWeight: 500,
    padding: "6px",
    height: "36px",
    "&:first-of-type": {
      borderTopLeftRadius: "0",
      borderBottomLeftRadius: "8px",
    },
    "&:last-of-type": {
      borderTopRightRadius: "0",
      borderBottomRightRadius: "8px",
    },
  },
  "& .add-row-section": {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    "& .MuiIconButton-root": {
      padding: "6px",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      borderRadius: "4px",
      color: "#fff",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.2)",
      },
    },
    "& .MuiSvgIcon-root": {
      fontSize: "20px",
      color: "#fff",
    },
    "& .MuiTypography-root": {
      fontSize: "0.75rem",
      opacity: 0.85,
      fontWeight: 400,
      color: "rgba(255, 255, 255, 0.85)",
    },
  },
  "& .total-value": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    padding: "4px 8px",
    borderRadius: "4px",
    minWidth: "24px",
    display: "inline-block",
    textAlign: "center",
    fontSize: "0.7rem",
  },
}));

export const StyledDropdown = styled(Dropdown)({
  "& .MuiInputBase-root": {
    fontSize: "0.7rem",
    height: "32px",
    backgroundColor: "#fff",
    "& .MuiSelect-select": {
      padding: "4px 8px",
      textAlign: "center !important",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "& .MuiSelect-icon": {
      position: "absolute",
      right: "4px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
});

export const WeekSelector = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "6px",

  "& .week-dropdown": {
    backgroundColor: "#FFFFFF",
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    padding: "6px 10px",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    minWidth: "180px",
    maxWidth: "220px",
    height: "32px",
    position: "relative",
    paddingRight: "28px",

    "& .week-text": {
      fontSize: "12px",
      fontWeight: 500,
      color: "#1A1A1A",
      flex: 1,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "block",
    },

    "& .dropdown-arrow": {
      position: "absolute",
      right: "8px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#666666",
      fontSize: "14px",
      flexShrink: 0,
    },

    "&:hover": {
      borderColor: "#BDBDBD",
    },
  },

  "& .nav-button": {
    width: "24px",
    height: "24px",
    minWidth: "24px",
    backgroundColor: "#5F80F6",
    borderRadius: "50%",
    border: "none",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",

    "&.Mui-disabled": {
      backgroundColor: "#E0E0E0",
      cursor: "not-allowed",

      "& .MuiSvgIcon-root": {
        color: "#999999",
      },
    },

    "& .MuiSvgIcon-root": {
      width: "16px",
      height: "16px",
      minWidth: "16px",
      minHeight: "16px",
      color: "#FFFFFF",
      transform: "scale(0.8)",
      flexShrink: 0,
    },

    "&:hover:not(.Mui-disabled)": {
      backgroundColor: "#4B66CC",
    },

    "&.left-arrow": {
      marginLeft: "6px",
    },
    "&.right-arrow": {
      marginLeft: "3px",
    },
  },
}));

export const StyledInputField = styled(InputField)({
  "& .MuiInputBase-root": {
    fontSize: "0.75rem",
    height: "36px",
    backgroundColor: "transparent",
    textAlign: "center",
    "& input": {
      textAlign: "center",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "6px 8px",
    textAlign: "center",
  },
});

export const HeaderBox = styled(Box)(({ theme }) => ({
  marginBottom: "16px",
  display: "flex",
  paddingLeft: "8px",
  padding: "8px",
  justifyContent: "space-between",
  alignItems: "center",
  "& .title": {
    fontSize: "0.75rem",
    color: "#666",
    marginBottom: "6px",
  },
  "& .week-section": {
    "& .week-text": {
      fontSize: "0.875rem",
      color: "#333",
      fontWeight: 500,
    },
  },
  "& .manager-section": {
    textAlign: "left",
    "& .title": {
      fontSize: "14px",
      fontWeight: 600,
      color: "#1A1A1A",
      marginBottom: "4px",
      lineHeight: "20px",
    },
    "& .manager-name": {
      fontSize: "13px",
      color: "#666666",
      fontWeight: 600,
      lineHeight: "18px",
    },
  },
  "& .button-group": {
    display: "flex",
    gap: "12px",
  },
}));

export const MainContainer = styled(Box)(({ theme }) => ({
  marginBottom: "0px",
  justifyContent: "space-between",
  alignItems: "center",
  paddingTop: "16px",
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
}));

export const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    marginTop: "2px",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    border: "1px solid #E0E0E0",
    minWidth: "180px",
    maxWidth: "220px",
    maxHeight: "300px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
      "&:hover": {
        background: "#666",
      },
    },
  },
  "& .MuiMenuItem-root": {
    fontSize: "12px",
    padding: "8px 12px",
    color: "#1A1A1A",
    minHeight: "32px",
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: "1.4",
    overflow: "visible",

    "&.Mui-selected": {
      backgroundColor: "#F5F5F5",
      "&:hover": {
        backgroundColor: "#EEEEEE",
      },
    },

    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
  },
  "& .MuiList-root": {
    padding: "4px 0",
  },
}));

export const CommentPopoverContent = styled(Box)(({ theme }) => ({
  padding: "12px",
  width: "280px",
  backgroundColor: "#fff",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  borderRadius: "6px",

  "& .comment-header": {
    marginBottom: "8px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#333",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: "6px",
    borderBottom: "1px solid #eee",
  },

  "& .MuiTextField-root": {
    width: "100%",

    "& .MuiInputBase-root": {
      fontSize: "12px",
      backgroundColor: "#F8F9FA",
      borderRadius: "4px",

      "& .MuiInputBase-inputMultiline": {
        padding: "8px",
        lineHeight: "1.4",
      },
    },

    "& .MuiOutlinedInput-root": {
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#3F49A7",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#3F49A7",
        borderWidth: "1px",
      },
    },
  },
}));

export const CommentCell = styled(DataCell)(({ theme }) => ({
  position: "relative",
  padding: "2px 4px",
  width: "80px",
  backgroundColor: "#F5F5F5 !important",

  "& .comment-icon-wrapper": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    width: "18px",
    height: "18px",
    margin: "0 auto",
    borderRadius: "50%",
    transition: "all 0.2s ease",

    "&:hover": {
      backgroundColor: "rgba(63, 73, 167, 0.08)",
      transform: "scale(1.1)",
    },
  },

  "& .comment-icon": {
    color: "#999",
    fontSize: "14px",
    transition: "all 0.2s ease",

    "&.has-comment": {
      color: "#3F49A7",
    },
  },
}));

export const DetailRow = styled(TableRow)(({ theme }) => ({
  "& td": {
    padding: 0,
    border: "none",
  },
}));

export const DetailContent = styled(Box)(({ theme }) => ({
  padding: "16px 0",
  backgroundColor: "#F8F9FA",
  borderRadius: "0 0 8px 8px",
  "& .MuiTableContainer-root": {
    backgroundColor: "#F5F5F5",
    overflowX: "auto",
  },
}));

export const DetailTable = styled(Table)(({ theme }) => ({
  backgroundColor: "#F5F5F5",
  borderCollapse: "collapse",
  "& .MuiTable-root": {
    backgroundColor: "#F5F5F5",
  },
  "& .MuiTableHead-root": {
    "& .MuiTableCell-root": {
      backgroundColor: "#F5F5F5",
      color: "#666666",
      fontSize: "0.7rem",
      fontWeight: 600,
      padding: "8px 16px",
      borderBottom: "1px solid #E0E0E0",
      "&:first-of-type": {
        backgroundColor: "#F5F5F5",
      },
    },
  },
  "& .MuiTableBody-root": {
    "& .MuiTableRow-root": {
      backgroundColor: "#F5F5F5",
      "&:hover": {
        backgroundColor: "#EBEBEB",
      },
      "& .MuiTableCell-root": {
        backgroundColor: "#F5F5F5",
        fontSize: "0.75rem",
        color: "#333333",
        padding: "12px 16px",
        borderBottom: "1px solid #F0F0F0",
        "&:first-of-type": {
          backgroundColor: "#F5F5F5",
        },
        "&.budget-cell": {
          backgroundColor: "#F5F5F5",
          "& .budget-text": {
            backgroundColor: "#FFFFFF !important",
            padding: "4px 8px",
            borderRadius: "4px",
            display: "inline-block",
            minWidth: "45px",
            color: "#666666",
          },
        },
      },
      "& .day-cell": {
        padding: "1px",
        width: "56px",
        height: "30px",
        backgroundColor: "#EBEBEB",
        borderRadius: "0",
        "&:nth-of-type(4)": {
          borderTopLeftRadius: "8px",
          borderBottomLeftRadius: "8px",
        },
        "&:nth-of-type(10)": {
          borderTopRightRadius: "8px",
          borderBottomRightRadius: "8px",
        },
      },
    },
  },
  "& .date-cell": {
    textAlign: "center",
    padding: "4px 0px",
    minWidth: "60px",
    width: "60px",
    height: "42px",
    marginTop: "3px",
    marginBottom: "3px",
    border: "none",
    "& .MuiBox-root": {
      "&:nth-of-type(4)": {
        borderTopLeftRadius: "8px",
        borderBottomLeftRadius: "8px",
      },
      "&:nth-of-type(10)": {
        borderTopRightRadius: "8px",
        borderBottomRightRadius: "8px",
      },
    },
  },
}));

export const StatusChip = styled(Box)(({ status }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 8px",
  borderRadius: "4px",
  fontSize: "0.65rem",
  fontWeight: 600,
  textTransform: "uppercase",
  ...(status === "approved" && {
    backgroundColor: "rgba(46, 125, 50, 0.1)",
    color: "#2E7D32",
  }),
  ...(status === "rejected" && {
    backgroundColor: "rgba(211, 47, 47, 0.1)",
    color: "#D32F2F",
  }),
  ...(status === "pending" && {
    backgroundColor: "rgba(245, 124, 0, 0.1)",
    color: "#F57C00",
  }),
}));

export const getWeekDates = (fridayStr) => {
  if (!fridayStr) return [];

  try {
    const friday = new Date(fridayStr);
    if (isNaN(friday.getTime())) {
      console.error("Invalid friday date:", fridayStr);
      return [];
    }

    const saturday = new Date(friday);
    saturday.setDate(friday.getDate() - 6);

    const dates = [];
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(saturday);
      currentDate.setDate(saturday.getDate() + i);

      // Validate the current date
      if (isNaN(currentDate.getTime())) {
        console.error("Invalid current date generated:", currentDate);
        continue;
      }

      dates.push({
        day: ["SAT", "SUN", "MON", "TUE", "WED", "THU", "FRI"][i],
        date: `${currentDate.getDate()}/${currentDate.getMonth() + 1}`,
        fullDate: currentDate,
      });
    }

    return dates;
  } catch (error) {
    console.error("Error parsing date:", error);
    return [];
  }
};

export const CommentsHistoryPopover = styled(Box)(({ theme }) => ({
  width: "260px",
  ".comments-header": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    background: "linear-gradient(135deg, #5F80F6 0%, #4B66D1 100%)",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    backdropFilter: "blur(8px)",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "1px",
      background:
        "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
    },
    "& h6": {
      fontSize: "11px",
      fontWeight: 600,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      textTransform: "uppercase",
      letterSpacing: "0.7px",
      "& .icon": {
        fontSize: "13px",
      },
    },
  },
  ".comment-list": {
    maxHeight: "260px",
    overflowY: "auto",
    padding: "8px 6px",
    backgroundColor: "#FAFBFF",
    "&::-webkit-scrollbar": {
      width: "2px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(95, 128, 246, 0.2)",
      borderRadius: "10px",
      "&:hover": {
        background: "rgba(95, 128, 246, 0.4)",
      },
    },
  },
  ".comment-bubble": {
    padding: "6px 8px",
    borderRadius: "4px",
    marginBottom: "6px",
    maxWidth: "95%",
    position: "relative",
    transition: "all 0.2s ease",
    border: "1px solid transparent",
    boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
    "&:hover": {
      transform: "translateX(2px)",
      borderColor: "rgba(95, 128, 246, 0.08)",
      boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
    },
    "&.manager": {
      backgroundColor: "#F5F7FF",
      marginRight: "auto",
      borderLeft: "2px solid #5F80F6",
      "&::before": {
        content: '""',
        position: "absolute",
        left: "-1px",
        top: 0,
        bottom: 0,
        width: "2px",
        background: "linear-gradient(to bottom, #5F80F6, rgba(95,128,246,0.4))",
        borderRadius: "2px",
      },
    },
    "&.resource": {
      backgroundColor: "#F8FAFF",
      marginLeft: "auto",
      borderRight: "2px solid #4B66D1",
      "&::before": {
        content: '""',
        position: "absolute",
        right: "-1px",
        top: 0,
        bottom: 0,
        width: "2px",
        background: "linear-gradient(to bottom, #4B66D1, rgba(75,102,209,0.4))",
        borderRadius: "2px",
      },
    },
  },
  ".comment-text": {
    fontSize: "11px",
    color: "#2C3E50",
    marginBottom: "6px",
    lineHeight: "1.5",
    wordBreak: "break-word",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    letterSpacing: "0.2px",
  },
  ".comment-meta": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "9px",
    "& .user-name": {
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: "4px",
      padding: "3px 8px",
      borderRadius: "12px",
      backgroundColor: "rgba(95, 128, 246, 0.04)",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(95, 128, 246, 0.08)",
      },
      "& .user-icon": {
        fontSize: "10px",
      },
    },
    "& .manager-name": {
      color: "#5F80F6",
    },
    "& .resource-name": {
      color: "#4B66D1",
    },
    "& .comment-date": {
      color: "#94A3B8",
      fontSize: "8px",
      display: "flex",
      alignItems: "center",
      gap: "3px",
      padding: "2px 6px",
      borderRadius: "8px",
      backgroundColor: "rgba(148, 163, 184, 0.06)",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(148, 163, 184, 0.1)",
      },
      "& .time-icon": {
        fontSize: "9px",
        opacity: 0.7,
      },
    },
  },
  ".no-comments": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
    color: "#94A3B8",
    background:
      "linear-gradient(135deg, rgba(95,128,246,0.02) 0%, rgba(75,102,209,0.02) 100%)",
    borderRadius: "8px",
    "& .icon": {
      fontSize: "24px",
      color: "#5F80F6",
      marginBottom: "12px",
      opacity: 0.5,
      animation: "float 3s ease-in-out infinite",
    },
    "& .text": {
      fontSize: "10px",
      textAlign: "center",
      lineHeight: "1.5",
      maxWidth: "160px",
      color: "#64748B",
      fontWeight: 500,
      letterSpacing: "0.2px",
    },
  },
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0)",
    },
    "50%": {
      transform: "translateY(-6px)",
    },
  },
  ".comment-input-container": {
    padding: "8px 12px",
    borderTop: "1px solid rgba(0,0,0,0.08)",
    backgroundColor: "#FFFFFF",
    display: "flex",
    gap: "6px",
    alignItems: "flex-end",

    "& .comment-input": {
      flex: 1,
      maxWidth: "calc(100% - 34px)",
      "& .MuiInputBase-root": {
        backgroundColor: "#F8F9FA",
        fontSize: "12px",
        borderRadius: "16px",
        padding: "4px 12px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
        transition: "all 0.2s ease",
        minHeight: "32px",
        maxHeight: "80px",
        overflowY: "auto",

        "&:hover": {
          backgroundColor: "#F5F5F5",
          boxShadow: "0 2px 4px rgba(95, 128, 246, 0.06)",
        },
        "&.Mui-focused": {
          backgroundColor: "#FFFFFF",
          boxShadow: "0 3px 6px rgba(95, 128, 246, 0.1)",
        },
        "& .MuiOutlinedInput-input": {
          padding: "6px 8px",
          fontSize: "12px",
          lineHeight: "1.4",
          maxHeight: "80px",
          overflowY: "auto",
          "&::placeholder": {
            color: "#94A3B8",
            opacity: 0.8,
          },
          "&::-webkit-scrollbar": {
            width: "4px",
            height: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#94A3B8",
            borderRadius: "4px",
            "&:hover": {
              background: "#64748B",
            },
          },
        },
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0, 0, 0, 0.08)",
        borderRadius: "16px",
        transition: "all 0.2s ease",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(95, 128, 246, 0.3)",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#5F80F6",
        borderWidth: "1px",
      },
    },
    "& .send-button": {
      backgroundColor: "#5F80F6",
      width: "28px",
      height: "28px",
      minWidth: "28px",
      borderRadius: "50%",
      padding: 0,
      marginBottom: "2px",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      "& .MuiSvgIcon-root": {
        fontSize: "14px",
        color: "#FFFFFF",
        transition: "transform 0.2s ease",
      },

      "&:hover": {
        backgroundColor: "#4B66D1",
        transform: "scale(1.05)",
        "& .MuiSvgIcon-root": {
          transform: "translateX(1px)",
        },
      },
      "&:active": {
        transform: "scale(0.95)",
      },
      "&.Mui-disabled": {
        backgroundColor: "#E0E0E0",
        "& .MuiSvgIcon-root": {
          color: "#999999",
        },
      },
    },
  },
}));

export const commentPopoverProps = {
  elevation: 2,
  sx: {
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow:
      "0 4px 24px rgba(95, 128, 246, 0.12), 0 2px 8px rgba(0,0,0,0.02)",
    marginRight: "12px",
    backdropFilter: "blur(8px)",
    background: "rgba(255,255,255,0.98)",
    "&:hover": {
      boxShadow:
        "0 8px 32px rgba(95, 128, 246, 0.15), 0 2px 8px rgba(0,0,0,0.03)",
    },
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
};

export const closeButtonStyles = {
  padding: "4px",
  color: "rgba(255,255,255,0.9)",
  backdropFilter: "blur(4px)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.15)",
    transform: "scale(1.1) rotate(90deg)",
  },
  "&:active": {
    transform: "scale(0.95) rotate(90deg)",
  },
};
